import { createApi } from '@reduxjs/toolkit/query/react';
import { api } from 'constants/constant';
import { getHeaders } from 'auth/auth';
import { buildBaseQueryWithReauth } from '../baseQuery';

/**
 * Branch management API slice
 * Uses existing backend_v2 tenant-scoped branch endpoints
 * mounted at /api/v1/b2b/organizations/branches.
 */
export const branchManagementAPI = createApi({
  reducerPath: 'branchManagement',
  tagTypes: ['Branches', 'Branch'],
  baseQuery: buildBaseQueryWithReauth({
    baseUrl: `${api}/b2b/organizations`,
    prepareHeaders: (headers) => {
      const baseHeaders = getHeaders();
      Object.entries(baseHeaders).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          headers.set(key, value);
        }
      });
      return headers;
    },
  }),
  endpoints: (builder) => ({
    listBranches: builder.query({
      query: ({ includeInactive = false } = {}) => ({
        url: `/branches${includeInactive ? '?include_inactive=true' : ''}`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((branch) => ({ type: 'Branch', id: branch.id })),
              { type: 'Branches', id: 'LIST' },
            ]
          : [{ type: 'Branches', id: 'LIST' }],
    }),

    getBranch: builder.query({
      query: (branchId) => ({
        url: `/branches/${branchId}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'Branch', id }],
    }),

    createBranch: builder.mutation({
      query: (payload) => ({
        url: '/branches',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'Branches', id: 'LIST' }],
    }),

    updateBranch: builder.mutation({
      query: ({ branchId, ...changes }) => ({
        url: `/branches/${branchId}`,
        method: 'PUT',
        body: changes,
      }),
      invalidatesTags: (result, error, { branchId }) => [
        { type: 'Branch', id: branchId },
        { type: 'Branches', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useListBranchesQuery,
  useGetBranchQuery,
  useCreateBranchMutation,
  useUpdateBranchMutation,
} = branchManagementAPI;

